import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

export interface MultiSelectItem {
	id: string
	name: string
	code?: string
}

interface MultiSelectSearchDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: string
	description?: string
	items: MultiSelectItem[]
	selectedIds: string[]
	onSelectionChange: (ids: string[]) => void
}

function normalizeSearch(text: string): string {
	return text
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
}

export function MultiSelectSearchDialog({
	open,
	onOpenChange,
	title,
	description,
	items,
	selectedIds,
	onSelectionChange,
}: MultiSelectSearchDialogProps) {
	const [search, setSearch] = useState('')

	const normalizedSearch = normalizeSearch(search)
	const filteredItems = useMemo(
		() =>
			items.filter((item) => {
				if (!normalizedSearch) return true
				return (
					normalizeSearch(item.name).includes(normalizedSearch) ||
					(item.code && normalizeSearch(item.code).includes(normalizedSearch))
				)
			}),
		[items, normalizedSearch]
	)

	const toggle = (id: string) => {
		onSelectionChange(
			selectedIds.includes(id) ? selectedIds.filter((i) => i !== id) : [...selectedIds, id]
		)
	}

	const selectAll = () => onSelectionChange(items.map((i) => i.id))
	const deselectAll = () => onSelectionChange([])

	return (
		<Dialog
			open={open}
			onOpenChange={(open) => {
				if (!open) setSearch('')
				onOpenChange(open)
			}}
		>
			<DialogContent className="max-w-md max-h-[85vh] flex flex-col gap-0 p-0">
				<DialogHeader className="px-4 pt-4 pb-3">
					<DialogTitle>{title}</DialogTitle>
					{description && <DialogDescription>{description}</DialogDescription>}
					<div className="flex items-center justify-between pt-2">
						<span className="text-sm text-muted-foreground">
							{selectedIds.length} de {items.length} seleccionados
						</span>
						<div className="flex gap-2">
							<Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAll}>
								Todos
							</Button>
							<Button variant="ghost" size="sm" className="h-7 text-xs" onClick={deselectAll}>
								Ninguno
							</Button>
						</div>
					</div>
				</DialogHeader>
				<div className="px-4 pb-2">
					<div className="relative">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder="Buscar..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9 h-9"
							autoFocus
						/>
					</div>
				</div>
				<div className="flex-1 overflow-y-auto min-h-0 border-t">
					{filteredItems.length === 0 ? (
						<p className="text-sm text-muted-foreground p-4 text-center">
							No se encontraron resultados
						</p>
					) : (
						filteredItems.map((item) => (
							<label
								key={item.id}
								className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
							>
								<Checkbox
									checked={selectedIds.includes(item.id)}
									onCheckedChange={() => toggle(item.id)}
								/>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{item.name}</p>
									{item.code && (
										<p className="text-xs text-muted-foreground">{item.code}</p>
									)}
								</div>
							</label>
						))
					)}
				</div>
				<DialogFooter className="px-4 py-3 border-t">
					<Button onClick={() => onOpenChange(false)}>Listo</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
